import { PureComponent } from 'react';
import NavBar from '../components/common/NavBar';
import NewHeaderComponent from '../components/common/NewHeaderComponent';
import PatientReferralFilters, {
    PatientReferralFilterType,
    patientReferralFiltersObj,
} from './PatientReferralFilters';
import getReferralGivenDataByDocId from '../api/getReferralGivenDataByDocId';
import ReferralGivenTable from './ReferralGivenTable';
import { getAllPatientsUsingPids } from '../store/idbHandler';
import { Patient } from '../store/types/patient';
import { CreateReferalData } from '../api/createReferal';
import { track } from '../tracking';
import EmptyView from './profile/EmptyView';
import ReferralReceivedTable from './ReferralReceivedTable';
import getReferralReceivedDataByDocId from '../api/getReferralReceivedDataByDocId';
import { Doctor } from '../store/types/prescription';
import DoctorNetwork from './DoctorNetwork';

export type CreateReferalDataWithPatientData = CreateReferalData & {
    patient: Patient;
    _id?: string;
};
export type CreateReferalRecivedDataWithPatientData = CreateReferalData & {
    patient: Patient;
    referral_given_doc_profile?: Doctor;
    _id?: string;
};

type State = {
    referralGivenData?: CreateReferalDataWithPatientData[];
    referralReceivedData?: CreateReferalRecivedDataWithPatientData[];
    activeReferralFilter: PatientReferralFilterType;
    loading: boolean;
};

export class PatientReferralDashboard extends PureComponent<unknown, State> {
    state: State = {
        referralGivenData: undefined,
        referralReceivedData: undefined,
        activeReferralFilter: 'referrals-given',
        loading: false,
    };

    componentDidMount = async () => {
        this.setState({ loading: true });
        try {
            const referGivenData = await (await getReferralGivenDataByDocId())?.referData?.filter(
                (refer) => refer.type !== 'lab',
            );

            const patientIds: string[] = Array.from(
                new Set(referGivenData?.map((patient) => patient.patientid)),
            ).filter(Boolean) as string[];

            const referralPatient = (await getAllPatientsUsingPids(patientIds)) || [];

            const allPatientObject = referralPatient?.reduce((prevValue, currValue) => {
                if (currValue.id) {
                    prevValue[currValue.id] = currValue;
                }

                return prevValue;
            }, {} as { [key: string]: Patient });

            const referralDataWithPatientData = referGivenData
                ?.map((refer) => {
                    return {
                        ...refer,
                        patient: allPatientObject[refer.patientid || ''],
                    };
                })
                ?.filter((refer) => refer?.patient)
                ?.sort((a, b) => {
                    const dateA = a.created_at ? new Date(a.created_at) : null;
                    const dateB = b.created_at ? new Date(b.created_at) : null;

                    if (!dateA && dateB) {
                        return 1; // Place entries with undefined created_at at the bottom
                    }
                    if (dateA && !dateB) {
                        return -1; // Place entries with undefined created_at at the bottom
                    }

                    return (dateB as any) - (dateA as any);
                });

            this.setState({
                referralGivenData: referralDataWithPatientData,
            });
        } catch (error) {
            console.error(error);
            track('ERROR', { on: 'PatientReferralDashboard', error: (error as Error).message });
        }

        try {
            const referralReceivedData = await (
                await getReferralReceivedDataByDocId()
            )?.referData?.filter((referData) => referData?.type !== 'lab');

            const patientIds: string[] = Array.from(
                new Set(referralReceivedData?.map((patient) => patient.patientid)),
            ).filter(Boolean) as string[];

            const referralPatient = (await getAllPatientsUsingPids(patientIds)) || [];

            const allPatientObject = referralPatient?.reduce((prevValue, currValue) => {
                if (currValue.id) {
                    prevValue[currValue.id] = currValue;
                }

                return prevValue;
            }, {} as { [key: string]: Patient });

            const referralReceivedDataWithPatientData = referralReceivedData
                ?.map((refer) => {
                    return {
                        ...refer,
                        patient: allPatientObject[refer.patientid || ''],
                    };
                })
                ?.filter((refer) => refer?.patient)
                ?.sort((a, b) => {
                    const dateA = a.created_at ? new Date(a.created_at) : null;
                    const dateB = b.created_at ? new Date(b.created_at) : null;

                    if (!dateA && dateB) {
                        return 1; // Place entries with undefined created_at at the bottom
                    }
                    if (dateA && !dateB) {
                        return -1; // Place entries with undefined created_at at the bottom
                    }

                    return (dateB as any) - (dateA as any);
                });

            this.setState({
                referralReceivedData: referralReceivedDataWithPatientData,
            });
        } catch (error) {
            console.error(error);
            track('ERROR', {
                on: 'PatientReferralDashboard => referral received',
                error: (error as Error).message,
            });
        }

        this.setState({ loading: false });
    };

    setActiveFilter = (type: PatientReferralFilterType) => {
        this.setState({ activeReferralFilter: type });
    };

    render() {
        const {
            referralGivenData,
            referralReceivedData,
            activeReferralFilter,
            loading,
        } = this.state;

        return (
            <>
                <div className="grid h-full overflow-x-auto overflow-y-hidden grid-cols-queueGrid min-w-840">
                    <NavBar />
                    <div className="flex flex-col h-full col-span-11">
                        <NewHeaderComponent title="My Patient Referrals" />
                        <div className="flex flex-col flex-1 p-16">
                            <div className="flex items-center justify-between">
                                <PatientReferralFilters
                                    activeFilter={activeReferralFilter}
                                    isLoading={loading}
                                    setFilter={this.setActiveFilter}
                                    filterData={{
                                        'referrals-given': {
                                            count: referralGivenData?.length || 0,
                                        },
                                        'referrals-received': {
                                            count: referralReceivedData?.length || 0,
                                        },
                                    }}
                                />

                                <DoctorNetwork
                                    referralGivenData={referralGivenData}
                                    referralReceivedData={referralReceivedData}
                                />
                            </div>
                            {activeReferralFilter === 'referrals-given' && (
                                <div className="mt-8 flex flex-1 border-1 border-darwin-neutral-200">
                                    {!referralGivenData?.length ? (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <EmptyView
                                                name={
                                                    patientReferralFiltersObj?.[
                                                        activeReferralFilter
                                                    ]?.label
                                                }
                                            />
                                        </div>
                                    ) : (
                                        <ReferralGivenTable referralGivenData={referralGivenData} />
                                    )}
                                </div>
                            )}

                            {activeReferralFilter === 'referrals-received' && (
                                <div className="mt-8 flex flex-1 border-1 border-darwin-neutral-200">
                                    {!referralReceivedData?.length ? (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <EmptyView
                                                name={
                                                    patientReferralFiltersObj?.[
                                                        activeReferralFilter
                                                    ]?.label
                                                }
                                            />
                                        </div>
                                    ) : (
                                        <ReferralReceivedTable
                                            referralReceivedData={referralReceivedData}
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </>
        );
    }
}

export default PatientReferralDashboard;
